import {
  bandOf,
  higherBand,
  rirBand,
  rpeBand,
  type IntensityBand,
} from './programIntensity.js';
import type {
  PlannedExerciseEntry,
  PlannedSet,
} from './schemas/plan.js';

// Pure derivation for the plan-preview intensity matrix. No React, no i18n,
// no catalog — those live in the component. Kept pure so the band mapping +
// matrix shape are jest-locked (planIntensity.test.ts) before the
// presentational layer renders them.
//
// A plan is a SINGLE day, so there is no per-week periodization to colour
// like a program. The analogous "shape at a glance" is the workout's own
// per-exercise × per-set intensity: rows = exercises, columns = set index,
// each cell coloured by that set's band. It reuses the SAME band thresholds
// as the program grid (programIntensity.ts) so the two previews speak one
// visual language. Sets prescribed by fixed kg / history / nothing carry no
// placeable intensity → no band (the cell stays neutral; the load still
// shows in the per-exercise detail).

const fmt = (n: number): string => String(Math.round(n * 100) / 100);

export type PlanCell = {
  band: IntensityBand | null;
  // Short value always rendered on the cell: %-of-MAX → "78", RPE → "9",
  // RIR → "2". null = no banded signal.
  value: string | null;
  // True for a real prescribed set, false for right-padding cells. A real
  // set with no banded intensity (fixed kg / history / free) renders
  // outlined — prescribed work must never look like an empty cell.
  prescribed: boolean;
};

// One planned set's band + display value, from whichever RELATIVE signal it
// carries. Priority %-of-MAX → RPE → RIR matches the program resolver, which
// emits a single signal per set; a hand-authored set that mixes them keeps
// the load as the colour driver (the detail line still lists both). RPE/RIR
// never reach `test` — that band marks an actual %-of-MAX peak (bandOf).
export const plannedSetCell = (set: PlannedSet): PlanCell => {
  const w = set.targetWeight;
  if (w.kind === 'percentage') {
    return { band: bandOf(w.pctOfMax), value: fmt(w.pctOfMax), prescribed: true };
  }
  if (typeof set.targetRpe === 'number') {
    return { band: rpeBand(set.targetRpe), value: fmt(set.targetRpe), prescribed: true };
  }
  if (typeof set.targetRir === 'number') {
    return { band: rirBand(set.targetRir), value: fmt(set.targetRir), prescribed: true };
  }
  return { band: null, value: null, prescribed: true };
};

export type PlanExerciseRow = {
  exerciseId: string;
  nameSnapshot?: string;
  cells: PlanCell[]; // length === matrix.setCols (padded with neutral cells)
  band: IntensityBand | null; // hardest band across this exercise's sets
};

export type PlanIntensityMatrix = {
  setCols: number; // widest exercise's set count (column count)
  rows: PlanExerciseRow[];
};

// rows = exercises in author order (main lifts first by convention),
// columns = set index 1..setCols. An exercise with fewer sets than the
// widest one is right-padded with neutral cells so every row aligns under
// the shared set-number header.
export function buildPlanMatrix(
  entries: PlannedExerciseEntry[],
): PlanIntensityMatrix {
  const setCols = entries.reduce((m, e) => Math.max(m, e.sets.length), 0);
  const rows = entries.map((e) => {
    const cells: PlanCell[] = [];
    for (let i = 0; i < setCols; i += 1) {
      const set = e.sets[i];
      cells.push(set ? plannedSetCell(set) : { band: null, value: null, prescribed: false });
    }
    const band = cells.reduce<IntensityBand | null>(
      (acc, c) => higherBand(acc, c.band),
      null,
    );
    return {
      exerciseId: e.exerciseId,
      nameSnapshot: e.exerciseName,
      cells,
      band,
    };
  });
  return { setCols, rows };
}
